import React from "react";
import { COUPON_BASES, COUPON_MODES, VALIDITY_MONTHS_MAX, upfrontBenefits, wholeToInt } from "./draftModel";
import { formatValidityMonths } from "../../common/format";
import { CheckField, Messages, NumberField, Section, SelectField } from "./fields";

const BLANK_RATE = { bps: "", floorMinor: "", capMinor: "" };

// "Valid for 3 years" under the validity field, or what a blank means.
const validityHelp = (text) => {
  if (text.trim() === "") {
    return "How long the rider has to use the coupon once it unlocks. Blank uses the service's default.";
  }
  const months = wholeToInt(text);
  return months !== null && months >= 1 && months <= VALIDITY_MONTHS_MAX
    ? `Valid for ${formatValidityMonths(months)}`
    : `1 to ${VALIDITY_MONTHS_MAX} months`;
};

const AddonsCouponSection = ({ form, setForm, msgs, locked }) => {
  const rule = form.pricingRule;
  const upfront = upfrontBenefits(form);

  const setRate = (key, field, value) =>
    setForm((prev) => ({
      ...prev,
      pricingRule: {
        ...prev.pricingRule,
        addonRates: {
          ...prev.pricingRule.addonRates,
          [key]: { ...(prev.pricingRule.addonRates[key] || BLANK_RATE), [field]: value },
        },
      },
    }));
  const setCoupon = (field, value) =>
    setForm((prev) => ({
      ...prev,
      pricingRule: {
        ...prev.pricingRule,
        couponRule: { ...prev.pricingRule.couponRule, [field]: value },
      },
    }));

  return (
    <Section
      id="addons"
      title="Add-ons and coupon"
      description="Each benefit paid upfront is priced from the MRP with its own rate, floor and cap. A coupon benefit needs a coupon rule."
    >
      <h6 className="warranty-subhead">Add-on rates</h6>
      {upfront.length === 0 && (
        <p className="warranty-help">No benefit is paid upfront, so there are no add-on rates.</p>
      )}
      {upfront.map((benefit) => {
        const rate = rule.addonRates[benefit.key] || BLANK_RATE;
        const base = `pricingRule.addonRates.${benefit.key}`;
        const id = (field) => `warranty-addon-${benefit.key}-${field}`;
        return (
          <div className="warranty-subcard" key={benefit.key}>
            <div className="warranty-subcard-head">
              <strong>{benefit.label || benefit.key}</strong>
              <span className="warranty-muted">{benefit.key}</span>
            </div>
            <Messages messages={msgs(base)} />
            <div className="row">
              <div className="col-md-4">
                <NumberField
                  id={id("bps")}
                  label="Share of MRP"
                  kind="percent"
                  value={rate.bps}
                  disabled={locked}
                  messages={msgs(`${base}.bps`)}
                  onChange={(value) => setRate(benefit.key, "bps", value)}
                />
              </div>
              <div className="col-md-4">
                <NumberField
                  id={id("floor")}
                  label="Floor"
                  kind="rupees"
                  value={rate.floorMinor}
                  disabled={locked}
                  messages={msgs(`${base}.floorMinor`)}
                  onChange={(value) => setRate(benefit.key, "floorMinor", value)}
                />
              </div>
              <div className="col-md-4">
                <NumberField
                  id={id("cap")}
                  label="Cap"
                  kind="rupees"
                  value={rate.capMinor}
                  disabled={locked}
                  messages={msgs(`${base}.capMinor`)}
                  onChange={(value) => setRate(benefit.key, "capMinor", value)}
                />
              </div>
            </div>
          </div>
        );
      })}

      <h6 className="warranty-subhead">Coupon rule</h6>
      <CheckField
        id="warranty-couponEnabled"
        label="This plan has a coupon rule"
        checked={rule.couponEnabled}
        disabled={locked}
        help="Needed when a benefit is a coupon; not allowed otherwise."
        messages={msgs("pricingRule.couponRule")}
        onChange={(value) =>
          setForm((prev) => ({ ...prev, pricingRule: { ...prev.pricingRule, couponEnabled: value } }))
        }
      />
      {rule.couponEnabled && (
        <div className="row">
          <div className="col-md-4">
            <SelectField
              id="warranty-coupon-basis"
              label="Coupon based on"
              value={rule.couponRule.basis}
              options={COUPON_BASES}
              disabled={locked}
              help={
                rule.couponRule.basis === "plan_price"
                  ? "The plan's one-time price incl. GST and upfront add-ons. Monthly buyers get the same coupon. 100% = what the rider pays."
                  : "The cycle's MRP in the OMS."
              }
              messages={msgs("pricingRule.couponRule.basis")}
              onChange={(value) => setCoupon("basis", value)}
            />
          </div>
          <div className="col-md-3">
            <NumberField
              id="warranty-coupon-bps"
              label={rule.couponRule.basis === "plan_price" ? "Share of plan price" : "Share of MRP"}
              kind="percent"
              value={rule.couponRule.bps}
              disabled={locked}
              messages={msgs("pricingRule.couponRule.bps")}
              onChange={(value) => setCoupon("bps", value)}
            />
          </div>
          <div className="col-md-5">
            <SelectField
              id="warranty-coupon-mode"
              label="Rounding"
              value={rule.couponRule.mode}
              options={COUPON_MODES}
              disabled={locked}
              messages={msgs("pricingRule.couponRule.mode")}
              onChange={(value) => setCoupon("mode", value)}
            />
          </div>
          <div className="col-md-2">
            <NumberField
              id="warranty-coupon-step"
              label="Step"
              kind="rupees"
              value={rule.couponRule.roundingStepMinor}
              disabled={locked}
              messages={msgs("pricingRule.couponRule.roundingStepMinor")}
              onChange={(value) => setCoupon("roundingStepMinor", value)}
            />
          </div>
          <div className="col-md-2">
            <NumberField
              id="warranty-coupon-ending"
              label="Ending"
              kind="rupees"
              value={rule.couponRule.roundingEndingMinor}
              disabled={locked}
              messages={msgs("pricingRule.couponRule.roundingEndingMinor")}
              onChange={(value) => setCoupon("roundingEndingMinor", value)}
            />
          </div>
          <div className="col-md-4">
            <NumberField
              id="warranty-coupon-validity"
              label="Coupon validity"
              kind="whole"
              unit="months"
              value={rule.couponRule.validityMonths}
              disabled={locked}
              help={validityHelp(rule.couponRule.validityMonths)}
              messages={msgs("pricingRule.couponRule.validityMonths")}
              onChange={(value) => setCoupon("validityMonths", value)}
            />
          </div>
        </div>
      )}
    </Section>
  );
};

export default AddonsCouponSection;
